// @ts-strict-ignore
import moment from 'moment';

import type { ServiceObject, ServicesByName, ReasonCodeGroup } from '~/flow/types';
import { ActionType } from '../constants';
import type {
  Action, Threshold,
} from '../types';
import { RuleValidationErrorTypes } from './constants';
import { datadogRum } from '@datadog/browser-rum';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';

export function validateThreshold({
  threshold,
  actions,
  services,
  reasonCodeGroups,
}: {
  threshold: Threshold;
  actions: Record<string, Action>;
  services: ServicesByName;
  reasonCodeGroups: ReasonCodeGroup[];
}) {
  const validationResult = {
    actionId: {
      valid: false,
      error: null,
    },
    left: {
      actionId: {
        valid: false,
        error: null,
      },
      attributeId: {
        valid: false,
        error: null,
      },
      attributeComponents: {
        valid: false,
        error: null,
      },
    },
    operator: {
      valid: false,
      error: null,
    },
    right: {
      actionId: {
        valid: false,
        error: null,
      },
      attributeId: {
        valid: false,
        error: null,
      },
      attributeComponents: {
        valid: false,
        error: null,
      },
      reasonCodeGroupId: {
        valid: false,
        error: null,
      },
      attributeValue: {
        valid: false,
        error: null,
      },
      leftTagActionResult: {
        valid: false,
        error: null,
      },
    },
  };

  if (!threshold) {
    return RuleValidationErrorTypes.NO_THRESHOLD;
  }

  if (!threshold.left?.actionId) {
    validationResult.left.actionId.error = RuleValidationErrorTypes.NO_LEFT_ACTION;
    return validationResult;
  }

  validationResult.left.actionId.valid = true;

  let leftAttributeType = 'decimal';
  const action = actions[threshold.left.actionId];
  if (action?.type === ActionType.SERVICE) {
    const { attributeId: leftAttributeId } = threshold.left;

    if (!leftAttributeId) {
      validationResult.left.attributeId.error = RuleValidationErrorTypes.NO_LEFT_ATTRIBUTE;
      return validationResult;
    }
    const leftActionService: ServiceObject = services[action.service.internalName];
    const matchingLeftAttribute = leftActionService?.attributes[leftAttributeId];

    if (!matchingLeftAttribute) {
      validationResult.left.attributeId.error = RuleValidationErrorTypes.INCORRECT_LEFT_ATTRIBUTE;
      return validationResult;
    }

    leftAttributeType = matchingLeftAttribute.dataType;
    validationResult.left.attributeId.valid = true;

    if (matchingLeftAttribute.isMultilevel
      && threshold.left.attributeComponents?.length <= 0) {
      validationResult.left.attributeComponents.error = RuleValidationErrorTypes
        .NO_LEFT_ATTRIBUTE_COMPONENTS;
    } else {
      validationResult.left.attributeComponents.valid = true;
    }
  }

  if (!threshold.right.actionId) {
    if (action?.type === ActionType.TAG
      && threshold.right.leftTagActionResult === null) {
      validationResult.right.leftTagActionResult.error = RuleValidationErrorTypes
        .NO_LEFT_TAG_ACTION_RESULT;
    }

    switch (leftAttributeType) {
      case 'string': {
        if (typeof threshold.right.attributeValue !== 'string'
          || threshold.right.attributeValue === undefined) {
          validationResult.right.attributeValue.error = RuleValidationErrorTypes
            .NO_RIGHT_ATTRIBUTE_VALUE;
        }
        break;
      }
      case 'decimal':
      case 'integer': {
        if (typeof threshold.right.attributeValue !== 'number'
          || threshold.right.attributeValue === undefined) {
          validationResult.right.attributeValue.error = RuleValidationErrorTypes
            .NO_RIGHT_ATTRIBUTE_VALUE;
        }
        break;
      }
      case 'boolean': {
        if (typeof threshold.right.attributeValue !== 'string'
          || threshold.right.attributeValue === undefined
          || !['true', 'false'].includes(threshold.right.attributeValue)) {
          validationResult.right.attributeValue.error = RuleValidationErrorTypes
            .NO_RIGHT_ATTRIBUTE_VALUE;
        }
        break;
      }
      case 'date': {
        if (typeof threshold.right.attributeValue !== 'string'
          || threshold.right.attributeValue === undefined
          || !moment(threshold.right.attributeValue).isValid()) {
          validationResult.right.attributeValue.error = RuleValidationErrorTypes
            .NO_RIGHT_ATTRIBUTE_VALUE;
        }
        break;
      }
      case 'timestamp': {
        if (typeof threshold.right.attributeValue !== 'string'
          || threshold.right.attributeValue === undefined
          || isNaN(parseFloat(threshold.right.attributeValue))) {
          validationResult.right.attributeValue.error = RuleValidationErrorTypes
            .NO_RIGHT_ATTRIBUTE_VALUE;
        }
        break;
      }
      case 'reasoncodes': {
        if (typeof threshold.right.reasonCodeGroupId !== 'number'
          || threshold.right.reasonCodeGroupId === undefined) {
          validationResult.right.reasonCodeGroupId.error = RuleValidationErrorTypes
            .NO_RIGHT_REASON_CODE_GROUP;
        }

        if (!reasonCodeGroups.find(group => group.id
          === threshold.right.reasonCodeGroupId)) {
          validationResult.right.reasonCodeGroupId.error = RuleValidationErrorTypes
            .REASON_CODE_GROUP_ID_DOESNT_EXIST;
        }
        break;
      }
    }

    validationResult.right.attributeValue.valid = true;
    validationResult.right.reasonCodeGroupId.valid = true;
  } else {
    const rightAction = actions[threshold.right.actionId];
    validationResult.right.actionId.valid = true;

    if (rightAction?.type === ActionType.SERVICE) {
      const { attributeId: rightAttributeId } = threshold.right;

      if (!rightAttributeId) {
        validationResult.right.attributeId.error = RuleValidationErrorTypes
          .NO_RIGHT_ATTRIBUTE_FOR_DYNAMIC_COMPARISON;
        return validationResult;
      }
      const rightActionService: ServiceObject = services[action.service.internalName];
      const matchingRightAttribute = rightActionService?.attributes[rightAttributeId];

      if (!matchingRightAttribute) {
        validationResult.right.attributeId.error = RuleValidationErrorTypes
          .INCORRECT_RIGHT_ATTRIBUTE_FOR_DYNAMIC_COMPARISON;
        return validationResult;
      }

      validationResult.right.attributeId.valid = true;
    }
  }

  return validationResult;
}

// todo: migrate callers to use smaller footprint function definition
export function captureSelfReferencingThresholdError(opts: {
  threshold: Threshold;
  userAction: 'create' | 'update' | 'remove';
  meta?: Object;
});

export function captureSelfReferencingThresholdError(opts: {
  thresholdIds: {
    publicId: Threshold['publicId'];
    actionId: Threshold['actionId'];
    leftActionId: Threshold['left']['actionId'];
    rightActionId: Threshold['right']['actionId'];
  };
  userAction: 'create' | 'update' | 'remove';
  meta?: Object;
});

export function captureSelfReferencingThresholdError(opts: {
  threshold?: Threshold;
  thresholdIds?: {
    publicId: Threshold['publicId'];
    actionId: Threshold['actionId'];
    leftActionId: Threshold['left']['actionId'];
    rightActionId: Threshold['right']['actionId'];
  };
  userAction: 'create' | 'update' | 'remove';
  meta?: Object;
}) {
  const {
    threshold, thresholdIds, userAction, meta,
  } = opts;

  const ids = thresholdIds || {
    publicId: threshold.publicId,
    actionId: threshold.actionId,
    leftActionId: threshold.left?.actionId,
    rightActionId: threshold.right?.actionId,
  };

  const error = new Error(`Self-referencing threshold ${userAction}d`);
  const context = {
    error,
    thresholdIds: ids,
    newEditor: true,
    url: window.location.pathname,
    originatedUrl: window.location.pathname,
    ...(meta && { meta }),
  };
  error.name = 'SelfReferencingThresholdError';
  Object.assign(error, context);

  datadogRum.addError(error, context);
  captureExceptionsToSentry(error);
}
